<script setup>
defineProps({ items: Array });
defineEmits(["view", "edit"]);

const formatCurrency = (v) =>
  new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(v || 0);

const paymentText = (s) =>
  ({ paid: "ชำระแล้ว", partial: "ชำระบางส่วน", overdue: "ค้างชำระ" }[s] || s);

const paymentBadge = (s) =>
  ({
    paid: "text-bg-success",
    partial: "text-bg-warning",
    overdue: "text-bg-danger",
  }[s]);

  
const residentTypeText = (type) =>
  type === "owner" ? "เจ้าของ" : "ผู้เช่า";

const escapeCSV = (value) => {
  if (value === null || value === undefined) return "";
  return `"${String(value).replace(/"/g, '""')}"`;
};

const exportToCSV = (items) => {
  if (!items.length) {
    alert("ไม่มีข้อมูลสำหรับ Export");
    return;
  }

  const headers = [
    "รหัส",
    "ชื่อ",
    "ประเภท",
    "ยูนิต",
    "เบอร์โทร",
    "อีเมล",
    "สถานะ",
    "ยอดค้าง",
  ];

  const rows = items.map((r) => [
    r.residentCode,
    r.name,
    residentTypeText(r.residentType),
    r.unitCode,
    r.phone,
    r.email,
    paymentText(r.paymentStatus),
    r.outstanding ?? 0,
  ]);

  const csv = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `residents-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

</script>

<template>
  <div class="card border-0 shadow-sm rounded-4">
    <div class="card-body p-0">
      <div
        class="px-4 pt-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div>
          <h5 class="fw-bold mb-1">รายการลูกบ้านทั้งหมด</h5>
          <div class="text-muted small">
            พบทั้งหมด {{ items?.length ?? 0 }} รายการ
          </div>
        </div>
        <button
          type="button"
          class="btn btn-outline-danger border-danger-subtle rounded-3 shadow-sm"
          :disabled="!items.length"
          @click="exportToCSV(items)"
        >
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">รหัส</th>
              <th>ชื่อ</th>
              <th>ประเภท</th>
              <th>ยูนิต</th>
              <th>ติดต่อ</th>
              <th>สถานะ</th>
              <th class="text-end">ยอดค้าง</th>
              <th class="text-center pe-4">จัดการ</th>
            </tr>
          </thead>

          <tbody v-if="items?.length">
            <tr v-for="r in items" :key="r.id">
              <td class="ps-4 fw-semibold">{{ r.residentCode }}</td>
              <td>{{ r.name }}</td>
              <td>
                <span class="badge text-bg-light border">
                  {{ r.residentType === "owner" ? "เจ้าของ" : "ผู้เช่า" }}
                </span>
              </td>
              <td>{{ r.unitCode }}</td>
              <td>
                <div>{{ r.phone }}</div>
                <small class="text-muted">{{ r.email }}</small>
              </td>
              <td>
                <span class="badge" :class="paymentBadge(r.paymentStatus)">
                  {{ paymentText(r.paymentStatus) }}
                </span>
              </td>
              <td class="text-end">
                <span
                  :class="
                    r.outstanding > 0
                      ? 'text-danger fw-semibold'
                      : 'text-success fw-semibold'
                  "
                >
                  {{ formatCurrency(r.outstanding) }}
                </span>
              </td>
              <td class="text-center pe-4">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-light border"
                    title="ดูรายละเอียด"
                    @click="$emit('view', r)"
                  >
                    <i class="bi bi-eye text-primary-emphasis"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-light border"
                    title="แก้ไข"
                    @click="$emit('edit', r)"
                  >
                    <i class="bi bi-pencil-square text-warning-emphasis"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="8" class="text-center py-5 text-muted">
                <i class="bi bi-people fs-2 d-block mb-2"></i>
                ไม่พบข้อมูลลูกบ้าน
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  white-space: nowrap;
  font-size: 0.9rem;
}
</style>
